import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const AuthContext = createContext(null);

const readStoredUser = () => {
  try {
    const token = localStorage.getItem('token');
    const saved = localStorage.getItem('user');
    return token && saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const clearStoredSession = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Per-browser reading of who used what — don't hand it to the next
    // person who signs in on a shared machine.
    localStorage.removeItem('bib-recent-tools');
  } catch {
    // Nothing we can do; the in-memory session is cleared either way.
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkAuth = async () => {
      const stored = readStoredUser();
      if (!stored) {
        setLoading(false);
        return;
      }

      try {
        // The token may have expired since the last visit.
        await api.get('/auth/verify');
        setUser(stored);
      } catch {
        clearStoredSession();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = (userData, token) => {
    // Whatever the last person on this browser had cached isn't theirs.
    queryClient.clear();
    setUser(userData);
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch {
      // Session works for this tab; it just won't survive a refresh.
    }
  };

  const logout = () => {
    setUser(null);
    clearStoredSession();
    queryClient.clear();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
